/**
 * SMS Integration Service
 * Twilio / MessageMedia abstraction for SMS notifications
 * Emergency notifications, appointment reminders, staff alerts
 */

import LocalStorageService from '../storage/LocalStorageService';

class SMSService {
  constructor() {
    this.storageKey = 'sms_messages';
    this.templateKey = 'sms_templates';
    this.provider = 'twilio'; // 'twilio' | 'messagemedia'
    this.senderNumber = '+61400000000';
    this.initializeTemplates();
  }

  initializeTemplates() {
    const existing = LocalStorageService.get(this.templateKey, []);
    if (existing.length > 0) return;

    const templates = [
      {
        id: 'sms_emergency',
        name: 'Emergency Notification',
        body: 'URGENT: {{residentName}} at {{facilityName}} — {{emergencyType}}. Contact facility immediately: {{facilityPhone}}',
        category: 'emergency',
        priority: 'critical'
      },
      {
        id: 'sms_appointment',
        name: 'Appointment Reminder',
        body: 'Reminder: {{residentName}} has an appointment with {{providerName}} on {{date}} at {{time}}. Location: {{location}}',
        category: 'appointment',
        priority: 'normal'
      },
      {
        id: 'sms_medication_change',
        name: 'Medication Change Alert',
        body: 'MedFast: Medication update for {{residentName}}. {{changeDescription}}. Contact {{facilityPhone}} for details.',
        category: 'clinical',
        priority: 'high'
      },
      {
        id: 'sms_shift_reminder',
        name: 'Shift Reminder',
        body: 'MedFast Roster: Your {{shiftType}} shift at {{facilityName}} starts at {{startTime}} on {{date}}. Please confirm attendance.',
        category: 'roster',
        priority: 'normal'
      },
      {
        id: 'sms_credential_expiry',
        name: 'Credential Expiry Alert',
        body: 'MedFast HR: Your {{credentialType}} expires on {{expiryDate}}. Please renew to maintain rostering eligibility. Contact HR.',
        category: 'hr',
        priority: 'high'
      },
      {
        id: 'sms_family_update',
        name: 'Family Update',
        body: 'MedFast: Update for {{residentName}} — {{updateSummary}}. Log in to the Family Portal for details or call {{facilityPhone}}.',
        category: 'family',
        priority: 'normal'
      }
    ];

    templates.forEach(t => LocalStorageService.addItem(this.templateKey, t));
  }

  async send(smsData) {
    try {
      const message = {
        id: `sms-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        to: smsData.to,
        from: this.senderNumber,
        body: smsData.body || '',
        templateId: smsData.templateId || null,
        templateVariables: smsData.templateVariables || {},
        category: smsData.category || 'general',
        priority: smsData.priority || 'normal',
        facilityId: smsData.facilityId || null,
        residentId: smsData.residentId || null,
        staffId: smsData.staffId || null,
        sentBy: smsData.sentBy || null,
        status: 'delivered',
        provider: this.provider,
        segments: 1,
        cost: 0.08,
        sentAt: new Date().toISOString(),
        deliveredAt: new Date().toISOString(),
        failedAt: null,
        errorCode: null
      };

      if (message.templateId) {
        const template = await this.getTemplate(message.templateId);
        if (template.success) {
          message.body = this._interpolate(template.data.body, message.templateVariables);
          message.category = template.data.category;
          message.priority = template.data.priority;
        }
      }

      message.segments = Math.ceil(message.body.length / 160);
      message.cost = message.segments * 0.08;

      const created = LocalStorageService.addItem(this.storageKey, message);
      console.log(`[SMSService] Sent SMS to ${message.to}: ${message.body.substring(0, 50)}...`);
      return { success: true, data: created };
    } catch (error) {
      console.error('[SMSService] Send failed:', error);
      return { success: false, error: error.message };
    }
  }

  async sendBulk(recipientList, templateId, baseVariables = {}) {
    try {
      const results = [];
      for (const recipient of recipientList) {
        const variables = { ...baseVariables, ...recipient.variables };
        const result = await this.send({
          to: recipient.phone,
          templateId,
          templateVariables: variables,
          category: 'bulk',
          facilityId: baseVariables.facilityId
        });
        results.push({ phone: recipient.phone, ...result });
      }

      const sent = results.filter(r => r.success).length;
      return { success: true, data: { sent, failed: results.length - sent, total: recipientList.length, totalCost: results.reduce((sum, r) => sum + (r.data?.cost || 0), 0) } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async sendEmergencyBroadcast(facilityId, emergencyType, details) {
    try {
      const staffMessages = LocalStorageService.get('hr_staff_profiles', [])
        .filter(s => s.facilityId === facilityId && s.status === 'active' && s.emergencyContact?.phone)
        .map(s => ({
          phone: s.phone || s.emergencyContact.phone,
          variables: { staffName: s.firstName + ' ' + s.lastName }
        }));

      const result = await this.sendBulk(staffMessages, 'sms_emergency', {
        facilityId,
        emergencyType,
        facilityPhone: details.facilityPhone || '1300 MEDFAST'
      });

      return { success: true, data: { ...result.data, broadcastType: 'emergency', emergencyType } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getHistory(filters = {}) {
    try {
      let messages = LocalStorageService.get(this.storageKey, []);

      if (filters.facilityId) messages = messages.filter(m => m.facilityId === filters.facilityId);
      if (filters.category) messages = messages.filter(m => m.category === filters.category);
      if (filters.status) messages = messages.filter(m => m.status === filters.status);
      if (filters.startDate) messages = messages.filter(m => m.sentAt >= filters.startDate);
      if (filters.endDate) messages = messages.filter(m => m.sentAt <= filters.endDate);

      messages.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
      return { success: true, data: messages };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getTemplate(templateId) {
    try {
      const templates = LocalStorageService.get(this.templateKey, []);
      const template = templates.find(t => t.id === templateId);
      return template ? { success: true, data: template } : { success: false, error: 'Template not found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getStats(facilityId = null, period = 30) {
    try {
      let messages = LocalStorageService.get(this.storageKey, []);
      if (facilityId) messages = messages.filter(m => m.facilityId === facilityId);

      const cutoff = new Date(Date.now() - period * 24 * 60 * 60 * 1000).toISOString();
      messages = messages.filter(m => m.sentAt >= cutoff);

      return {
        success: true,
        data: {
          totalSent: messages.length,
          delivered: messages.filter(m => m.status === 'delivered').length,
          failed: messages.filter(m => m.status === 'failed').length,
          totalSegments: messages.reduce((sum, m) => sum + (m.segments || 1), 0),
          totalCost: messages.reduce((sum, m) => sum + (m.cost || 0), 0).toFixed(2),
          byCategory: messages.reduce((acc, m) => { acc[m.category] = (acc[m.category] || 0) + 1; return acc; }, {}),
          byPriority: messages.reduce((acc, m) => { acc[m.priority] = (acc[m.priority] || 0) + 1; return acc; }, {})
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _interpolate(template, variables) {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key) => variables[key] || `{{${key}}}`);
  }
}

export default new SMSService();
